import { Link } from "react-router-dom";
import { Instagram, Facebook, Linkedin, Youtube, MapPin, Phone, Mail, MessageCircle } from "lucide-react";
import { Logo } from "./Logo";
import { LangSwitch } from "./Header";
import { useLanguage } from "@/i18n/LanguageContext";
import { ADDRESS, EMAIL, PHONE, PHONE_LINK, WHATSAPP, WA_LINK } from "@/data/content";

const SOCIALS = [
  { name: "Instagram", icon: Instagram },
  { name: "Facebook", icon: Facebook },
  { name: "LinkedIn", icon: Linkedin },
  { name: "YouTube", icon: Youtube },
];

const NAV = [
  ["nav.home", "/"], ["nav.about", "/about"], ["nav.services", "/services"],
  ["nav.projects", "/projects"], ["nav.process", "/process"], ["nav.estimate", "/estimate"],
  ["nav.contact", "/contact"],
];

export const Footer = () => {
  const { t } = useLanguage();
  return (
    <footer className="bg-ink text-offwhite" data-testid="site-footer">
      <div className="mx-auto grid max-w-7xl gap-14 px-5 py-20 md:grid-cols-2 md:px-8 lg:grid-cols-4">
        <div>
          <Logo />
          <p className="mt-5 font-display text-xl italic text-gold">{t("footer.tagline")}</p>
          <div className="mt-6 flex gap-3">
            {SOCIALS.map(({ name, icon: Icon }) => (
              <button
                key={name}
                type="button"
                data-testid={`social-${name.toLowerCase()}-button`}
                title={`${name} — ${t("footer.socialSoon")}`}
                aria-label={`${name} (${t("footer.socialSoon")})`}
                className="rounded-full border border-white/15 p-2.5 text-white/50 transition-colors hover:border-gold hover:text-gold"
              >
                <Icon className="h-4 w-4" />
              </button>
            ))}
          </div>
        </div>
        <nav aria-label="Footer">
          <p className="eyebrow-dark mb-6">Navigate</p>
          <ul className="space-y-3">
            {NAV.map(([key, to]) => (
              <li key={to}>
                <Link to={to} data-testid={`footer-${key.split(".")[1]}-link`} className="text-sm text-white/60 transition-colors hover:text-gold">
                  {t(key)}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div>
          <p className="eyebrow-dark mb-6">{t("contact.eyebrow")}</p>
          <ul className="space-y-4 text-sm text-white/60">
            <li className="flex gap-3"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" />{ADDRESS}</li>
            <li>
              <a href={PHONE_LINK} data-testid="footer-phone-link" className="flex gap-3 transition-colors hover:text-gold">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-gold" />{PHONE}
              </a>
            </li>
            <li>
              <a href={WA_LINK()} target="_blank" rel="noreferrer" data-testid="footer-whatsapp-link" className="flex gap-3 transition-colors hover:text-gold">
                <MessageCircle className="mt-0.5 h-4 w-4 shrink-0 text-gold" />{WHATSAPP}
              </a>
            </li>
            <li className="flex gap-3 break-all">
              <Mail className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              <a href={`mailto:${EMAIL}`} data-testid="footer-email-link" className="transition-colors hover:text-gold">{EMAIL}</a>
            </li>
          </ul>
        </div>
        <div>
          <p className="eyebrow-dark mb-6">Language</p>
          <LangSwitch />
          <p className="mt-4 text-xs text-white/40">{t("footer.kannadaNote")}</p>
          <div className="mt-8 flex flex-col gap-3 text-sm">
            <Link to="/privacy" data-testid="footer-privacy-link" className="text-white/60 transition-colors hover:text-gold">{t("footer.privacy")}</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="text-white/60 transition-colors hover:text-gold">{t("footer.terms")}</Link>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10 py-6 text-center text-xs text-white/40">
        {t("footer.rights")}
      </div>
    </footer>
  );
};
